<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, reactive, ref, watch } from 'vue'
import {
  buyerRejectTransactionAction,
  confirmBuyerBankAccountsAction,
} from '@/api/functions/transactions/transaction-status.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  transaction: { type: Object },
  buyerIranianSrc: { type: Array },
  buyerIranian: { type: Array },
  buyerRevolut: { type: Array },
  buyerPaypal: { type: Array },
  buyerInternational: { type: Array },
  buyerWise: { type: Array },
})

const emit = defineEmits(['accept', 'reject', 'createAccount'])

const toast = useToast()
const queryClient = useQueryClient()

const form = reactive({
  iranianSourceBank: null,
  internationalRevolutBank: null,
  internationalBank: null,
  internationalPaypalBank: null,
  internationalWiseBank: null,
})

watch(
  () => [props.transaction],
  ([transaction]) => {
    if (transaction) {
      form.iranianSourceBank = transaction.bankAccounts?.buyerIranianSourceBank?.id || null
      form.internationalRevolutBank = transaction.bankAccounts?.buyerInternationalRevolutBank?.id || null
      form.internationalBank = transaction.bankAccounts?.buyerInternationalBank?.id || null
      form.internationalPaypalBank = transaction.bankAccounts?.buyerInternationalPaypalBank?.id || null
      form.internationalWiseBank = transaction.bankAccounts?.buyerInternationalWiseBank?.id || null
    }
  },
  { immediate: true }
)

// const isOwner = computed(() => props.transaction?.offer.owner.id === props.transaction?.buyer.id)

// const isDisabled = () => isOwner.value ? props.transaction?.offerOwnerAcceptance : props.transaction?.requisitionOwnerAcceptance;
const isDisabled = computed(() => {
  if (props.transaction?.requisition?.type === 'BUY') {
    return props.transaction?.requisitionOwnerAcceptance
  }

  return props.transaction?.offerOwnerAcceptance
})

const buyerAcceptMutation = useMutationApi({
  mutationFn: async () => {
    const uuid = props.transaction?.uuid
    if (!uuid) throw new Error('شناسه تراکنش یافت نشد')

    return confirmBuyerBankAccountsAction(uuid, {
      iranian_source_bank_account_id: form.iranianSourceBank,
      international_bank_account_id: form.internationalBank,
      international_paypal_bank_account_id: form.internationalPaypalBank,
      international_revolut_bank_account_id: form.internationalRevolutBank,
      international_wise_bank_account_id: form.internationalWiseBank,
    })
  },

  onSuccess: () => {
    toast.success('حساب‌های خریدار با موفقیت تایید شد')
    emit('accept')
    queryClient.invalidateQueries({ queryKey: ['transactionDetails', props.transaction?.uuid] })
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err) || 'خطا در تایید حساب خریدار')
  },
})

const buyerRejectMutation = useMutationApi({
  mutationFn: async () => {
    const uuid = props.transaction?.uuid
    if (!uuid) throw new Error('شناسه تراکنش یافت نشد')

    return buyerRejectTransactionAction(uuid)
  },

  onSuccess: () => {
    toast.success('تراکنش توسط خریدار رد شد')
    emit('reject')
    queryClient.invalidateQueries({ queryKey: ['transactionDetails', props.transaction?.uuid] })
  },

  onError: (err: unknown) => {
    toast.error(getErrorMessage(err) || 'خطا در رد تراکنش توسط خریدار')
  },
})
</script>

<template>
  <div>
    <div
      class="mb-2.5 flex items-center justify-center text-[#2d5396] text-[15px] rounded-xs font-medium bg-[#fffbcf] h-[79px]">
      خریدار
    </div>

    <div v-if="transaction?.bankAccounts?.buyerIranianSourceBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب مبدا ریالی</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.iranianSourceBank" class="col-span-7" label="انتخاب حساب" :options="buyerIranianSrc"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'مبداء ریالی', null, true)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>

    <div v-if="transaction?.bankAccounts?.buyerInternationalRevolutBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب رولوت</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.internationalRevolutBank" class="col-span-7" label="انتخاب حساب" :options="buyerRevolut"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'بین المللی', 'رولوت', true)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>

    <div v-if="transaction?.bankAccounts?.buyerInternationalPaypalBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب پی پال</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.internationalPaypalBank" class="col-span-7" label="انتخاب حساب" :options="buyerPaypal"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'بین المللی', 'پی پال', true)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>

    <div v-if="transaction?.bankAccounts?.buyerInternationalBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب بین بانکی</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.internationalBank" class="col-span-7" label="انتخاب حساب" :options="buyerInternational"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'بین المللی', 'بین بانکی', true)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>
    <div v-if="transaction?.bankAccounts?.buyerInternationalWiseBank">
      <span class="text-[#2d5396] text-[15px] ms-3">حساب وایز</span>
      <div class="flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start mt-10 gap-6 lg:px-6">
        <Select v-model="form.internationalWiseBank" class="col-span-7" label="انتخاب حساب" :options="buyerWise"
          :disabled="isDisabled" />

        <Button :isAdmin="true" @click="() => emit('createAccount', 'بین المللی', 'وایز', true)" type="button"
          :disabled="isDisabled"
          class=" bg-transparent rounded-xl  cursor-pointer hover:bg-[#d7deeb] text-[#2d5396]! font-medium col-span-5 h-[46px]">
          <span class="flex items-center justify-center gap-2  text-nowrap">
            <img src="/svg/circle-plus.svg" />
            شماره حساب جدید
          </span>
        </Button>
      </div>
    </div>
    <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 lg:gap-0 mt-4">
      <div class="flex items-center text-[#2d5396] text-[15px] font-medium gap-6">
        <span class="">وضعیت تایید:</span>
        <div class="flex items-center" :class="isDisabled ? 'text-[#4caf50]' : 'text-[#f44336]'">
          <img width="16" height="16"
            :src="isDisabled ? '/svg/check-circle-svgrepo-com.svg' : '/svg/info-circle-svgrepo-com.svg'" />
          {{ isDisabled ? 'تایید شده' : 'هنوز تایید نشده' }}
        </div>
      </div>

      <div class="w-full lg:w-fit flex items-center justify-end lg:justify-start gap-1 mb-4">
        <Button @click="buyerAcceptMutation.mutate()" type="button" :disabled="isDisabled" label="تایید "
          :isAdmin="true" class="w-fit px-3!  h-8! rounded-lg! font-medium! text-[13px]!" />

        <Button @click="buyerRejectMutation.mutate()" type="button" :disabled="isDisabled" label="رد " variant="outline"
          :isAdmin="true" class="w-fit px-3!  h-8! rounded-lg! border-2 font-medium! text-[13px]!" />
      </div>
    </div>
  </div>
</template>
